import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { FormSelect } from '@components/common/FormSelect/FormSelect';
import { FormControl } from '@components/common/FormControl/FormControl';
import { ApplicationFormPageEnum } from '../types';
import { personalDetailsQuestions } from './questions';
import { useDatesOptions } from '@hooks/useDatesOptions';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { IPersonalDetailsForm } from './types';
import { ISelectOption } from '@components/common/Select/types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { getCurrentRoute } from '@utils/location';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { parseApplicationInProgress } from '../helpers';

export const PersonalDetails = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { daysOptions, monthOptions, generateLastXYearsOptions, retirentAgeRange } = useDatesOptions();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IPersonalDetailsForm>({
    defaultValues: {
      day: null,
      month: null,
      year: null,
      maritalStatus: null,
      retirementAge: null,
      firstTimeBuyer: null,
    },
  });

  const formState = form.watch();

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IPersonalDetailsForm) => {
    const dateKeys = ['day', 'month', 'year'];
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: dateKeys.includes(key)
        ? intl.formatMessage({ id: personalDetailsQuestions[key].labelKey })
        : `${intl.formatMessage({ id: personalDetailsQuestions[key].labelKey })} (${key})`,
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.CONTACT_DETAILS);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section>
          <FormattedMessage id={personalDetailsQuestions.day.labelKey} tagName={'h4'} />

          <div className={'grid grid-cols-3 gap-4'}>
            <div>
              <FormattedMessage id={'common.date'} tagName={'p'} />
              <FormSelect
                name={'day'}
                options={daysOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                    option.value === selectedOptionValue,
                  value: formState.day,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.selectDay' }),
                }}
                rules={{ required: requiredFieldErrorMessage }}
              />
            </div>
            <div>
              <FormattedMessage id={'common.month'} tagName={'p'} />
              <FormSelect
                name={'month'}
                options={monthOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                    option.value === selectedOptionValue,
                  value: formState.month,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.selectMonth' }),
                }}
                rules={{ required: requiredFieldErrorMessage }}
              />
            </div>
            <div>
              <FormattedMessage id={'common.year'} tagName={'p'} />
              <FormSelect
                name={'year'}
                options={generateLastXYearsOptions(100)}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                    option.value === selectedOptionValue,
                  value: formState.year,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.selectYear' }),
                }}
                rules={{ required: requiredFieldErrorMessage }}
              />
            </div>
          </div>
        </section>

        <section>
          <FormattedMessage id={personalDetailsQuestions.maritalStatus.labelKey} tagName={'h4'} />
          <FormSelect
            rules={{ required: requiredFieldErrorMessage }}
            name={'maritalStatus'}
            options={personalDetailsQuestions.maritalStatus.options}
            selectProps={{
              isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                option.value === selectedOptionValue,
              value: formState.maritalStatus,
            }}
            selectInputProps={{ placeholder: intl.formatMessage({ id: 'placeholders.selectMaritalStatus' }) }}
          />
        </section>

        <section>
          <FormattedMessage id={personalDetailsQuestions.retirementAge.labelKey} tagName={'h4'} />

          <div className={'grid grid-cols-3 gap-4'}>
            <FormSelect
              name={'retirementAge'}
              options={retirentAgeRange}
              selectProps={{
                isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                  option.value === selectedOptionValue,
                value: formState.retirementAge,
              }}
              selectInputProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.selectAge' }),
              }}
              rules={{ required: requiredFieldErrorMessage }}
            />
          </div>
        </section>

        <section>
          <FormattedMessage id={personalDetailsQuestions.firstTimeBuyer.labelKey} tagName={'h4'} />

          <div className={'grid grid-cols-3 gap-4'}>
            <FormControl
              name={'firstTimeBuyer'}
              radioOptions={personalDetailsQuestions.firstTimeBuyer.options}
              rules={{ required: requiredFieldErrorMessage }}
            />
          </div>
        </section>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
